import { saveGridLikeWorkbook } from "@maxis/client-logic/utils/componentUtil";

// Grid 내부 context menu compatibility class.
export class DataGridContextMenu {
  constructor(grid, fileName) {
    const d = new Date();
    this.grid = grid;
    this.fileName = `${fileName}_${d.getFullYear()}${d.getMonth() + 1}${d.getDate()}`;
    this.menu = this._buildMenu();

    const host = grid?.hostElement;
    if (!host) return;

    // Keep the handler so destroy() can detach it. An inline arrow here could never be
    // removed, which left one listener per grid mount alive for the session.
    this.host = host;
    this.onContextMenu = (e) => {
      const hit = grid.hitTest ? grid.hitTest(e) : null;
      if (this._isColumnHeader(hit)) {
        e.preventDefault();
        return;
      }

      this._selectHitCell(hit);
      e.preventDefault();
      this.menu.show(e);
    };
    host.addEventListener("contextmenu", this.onContextMenu, true);
  }

  /**
   * Must be called when the grid unmounts.
   *
   * _buildMenu() puts a div straight into document.body and registers a document-level
   * click listener; neither used to be removed, so every DataGrid mount leaked one node
   * on <body> plus one listener. Leaving stray nodes on <body> also matters because React
   * portals live there: extra siblings appearing and disappearing outside React's
   * knowledge is one way to get "removeChild: node is not a child of this node".
   */
  destroy() {
    if (this.host && this.onContextMenu) {
      this.host.removeEventListener("contextmenu", this.onContextMenu, true);
    }
    this.menu?.destroy?.();
    this.menu = null;
    this.host = null;
    this.onContextMenu = null;
    this.grid = null;
  }

  _isColumnHeader(hit) {
    const cellType = hit?.cellType;
    return cellType === "ColumnHeader" || cellType === 2 || hit?.panel === this.grid?.columnHeaders;
  }

  _selectHitCell(hit) {
    if (!hit || !this.grid?.select) return;
    const row = typeof hit.row === "number" ? hit.row : this.grid.selection?.row ?? 0;
    const col = typeof hit.col === "number" ? hit.col : this.grid.selection?.col ?? 0;
    if (row > -1 && col > -1) this.grid.select(row, col);
  }

  _buildMenu() {
    const menuElement = document.createElement("div");
    menuElement.className = "ctx-menu";
    menuElement.style.position = "fixed";
    menuElement.style.zIndex = "10000";
    menuElement.style.display = "none";
    menuElement.style.background = "#fff";
    menuElement.style.border = "1px solid #c8c8c8";
    menuElement.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.18)";
    menuElement.style.minWidth = "160px";

    const appendItem = (label, command) => {
      const item = document.createElement("button");
      item.type = "button";
      item.textContent = label;
      item.style.display = "block";
      item.style.width = "100%";
      item.style.border = "0";
      item.style.background = "transparent";
      item.style.padding = "7px 12px";
      item.style.textAlign = "left";
      item.style.cursor = "pointer";
      item.addEventListener("click", () => {
        this._executeCommand(command);
        menuElement.style.display = "none";
      });
      menuElement.appendChild(item);
    };

    appendItem("XLSX", "X_XLSX");
    appendItem("XLSX(Merge 제거)", "X_XLSX_NEW");
    document.body.appendChild(menuElement);

    const hideOnDocumentClick = () => {
      menuElement.style.display = "none";
    };
    document.addEventListener("click", hideOnDocumentClick);

    return {
      show: (e) => {
        menuElement.style.left = `${e.clientX}px`;
        menuElement.style.top = `${e.clientY}px`;
        menuElement.style.display = "block";
      },
      destroy: () => {
        document.removeEventListener("click", hideOnDocumentClick);
        menuElement.remove();
      },
    };
  }

  _executeCommand(cmd) {
    const mergeState = this.grid?.mergeManager;
    const mergingColumns = [];

    if (cmd === "X_XLSX_NEW" && this.grid?.columns) {
      this.grid.mergeManager = null;
      Array.from(this.grid.columns).forEach((column, idx) => {
        if (column.allowMerging) mergingColumns.push(idx);
        column.allowMerging = false;
      });
    }

    saveGridLikeWorkbook([this.grid], {
      fileName: `${this.fileName}.xlsx`,
      includeColumns: (column) => column.binding !== "ROW_CHECK" && column.binding !== "ROW_STATUS",
    }).finally(() => {
      if (cmd === "X_XLSX_NEW" && this.grid?.columns) {
        this.grid.mergeManager = mergeState;
        Array.from(this.grid.columns).forEach((column, idx) => {
          if (mergingColumns.includes(idx)) column.allowMerging = true;
        });
      }

      this.grid?.refresh?.();
      const sel = this.grid?.selection;
      const cell = this.grid?.cells?.getCellElement?.(sel?.row, sel?.col);
      cell?.focus?.();
    });
  }
}
